import cloneDeep from 'lodash/cloneDeep.js';
import filter from 'lodash/filter.js';
import findIndex from 'lodash/findIndex.js';
import has from 'lodash/has.js';
import merge from 'lodash/merge.js';
import sortBy from 'lodash/sortBy.js';
import toArray from 'lodash/toArray.js';

import Activity from '@/models/Activity.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import MenuService from '@/services/MenuService.js';
import BaseActivity from '@/store/modules/base/BaseActivity.js';
import Collection from '@/store/quill/Collection.js';
import ActivityHelper from '@/store/utilities/ActivityHelpers.js';
import { promiseError } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const helper = new ActivityHelper();

export default function ({ name }) {
    return merge(Collection(name, BaseActivity), {
        state: {},

        getters: {
            activityContent: state => (id, field) => {
                return state[id]?.activity_content?.[field];
            },

            getByKey(state, getters, rootState, rootGetters) {
                return ({ keyName, key, field }) => {
                    const uuid = Object.keys(state).find(
                        entry => getters.has(entry) && getters.get(entry)[keyName] === key
                    );

                    if (!field) {
                        return uuid ? rootGetters[`${name}/get`](uuid) : null;
                    }

                    return uuid ? rootGetters[`${name}/${uuid}/get`](field) : null;
                };
            },

            getWeekStatus:
                (state, getters, rootState, rootGetters) =>
                ({ uuid, template }) => {
                    const item = getters.get(uuid);

                    if (!item) return;

                    if (template) {
                        if (item.parent) {
                            const parent = rootGetters['SectionTemplates/get'](item.parent);

                            if (!parent) throw new Error('An error occurred, Edverum support has been notified');

                            return helper.getSectionDueDates(parent, rootGetters, template);
                        }
                    }

                    if (!template) {
                        if (item.section_id) {
                            const parent = rootGetters['Sections/getByKey']({
                                key: Number(item.section_id),
                                keyName: 'id',
                                field: null,
                            });

                            if (!parent) throw new Error('An error occurred, Edverum support has been notified');

                            return helper.getSectionDueDates(parent, rootGetters, template);
                        }

                        // New activities have `parent` (section uuid) before `section_id` is set (first save).
                        if (item.parent) {
                            const parent = rootGetters['Sections/get'](item.parent);

                            if (!parent) throw new Error('An error occurred, Edverum support has been notified');

                            return helper.getSectionDueDates(parent, rootGetters, template);
                        }
                    }

                    return { type: 'root-level', week: null };
                },

            nestedItem:
                state =>
                ({ id, item, field }) => {
                    const entry = state[id];
                    if (!entry) return null;
                    const nested = entry[item];
                    if (!nested) return null;
                    return nested[field] ?? null;
                },

            remediation: (state, getters) => id => {
                const remediationItems = filter(getters.all(), activity => {
                    const template = activity.type.includes('template');
                    const settingKey = template ? 'activity_settings_template' : 'activity_settings';
                    const courseIdKey = template ? 'course_template_id' : 'course_id';

                    return activity[settingKey].is_remediation && activity[courseIdKey] === id;
                });

                return sortBy(remediationItems, 'order');
            },
        },

        actions: {
            /**
             *
             * @param context
             * @param id
             * @returns {Promise<any>}
             */
            async fetchOne(context, id) {
                const queryBuilder = new QueryBuilder(`activities/${id}`)
                    .include(['course', 'course.gradebook', 'contentable', 'media', 'rubric'])
                    .raw({ show_password: true })
                    .raw({ append: 'completed' });

                try {
                    const response = await axios.get(queryBuilder.toUrl());

                    // Access root getter to see if user is in a quiz attempt
                    // this is important because we don't want to load menu data during a quiz attempt
                    // this will cause an error since the menu is not white-listed during quiz attempts
                    const userHasActiveQuiz = context.rootGetters['User/hasActiveQuiz'];

                    await Activity.insert({ data: response.data.data });

                    await context.dispatch('hydrateActivity', {
                        payload: response.data.data,
                    });

                    // Load menu data using MenuService if courseId is available
                    const courseId = response.data.data.course_id || response.data.data.course?.id;
                    if (courseId && !userHasActiveQuiz) {
                        try {
                            await MenuService.loadAndSetCourseMenu(context, courseId);
                        } catch (menuError) {
                            console.warn('Failed to load menu data:', menuError);
                        }
                    }

                    return response;
                } catch (error) {
                    promiseError({
                        key: 'activity_load_error',
                        context,
                    });

                    return error;
                }
            },

            async hydrateActivity(context, { payload }) {
                const temp = name.toLowerCase().includes('temp');
                const settings = temp ? 'activity_settings_template' : 'activity_settings';
                const grading_scheme_type = temp ? 'grading_scheme_template' : 'grading_scheme';
                const rubric_type = temp ? 'rubric_template' : 'rubric';
                const parent_key = temp ? 'section_template_id' : 'section_id';

                payload.displayStatus = { editing: false };

                if (has(payload, parent_key)) {
                    const parent_namesace = temp ? 'SectionTemplates' : 'Sections';

                    const parent_uuid = context.rootGetters[`${parent_namesace}/getByKey`]({
                        keyName: 'id',
                        key: Number(payload[parent_key]),
                        field: 'uuid',
                    });

                    payload.parent = parent_uuid;
                }

                if (payload.course) {
                    payload.gradebook = payload.course.gradebook;
                }

                if (payload[rubric_type]) {
                    const module = name.includes('Temp') ? 'TemplateRubrics' : 'Rubrics';

                    await context.dispatch(
                        `${module}/hydrateRubrics`,
                        {
                            payload,
                            rubric_type,
                        },
                        { root: true }
                    );
                }

                if (payload[grading_scheme_type]) {
                    const grading_scheme = cloneDeep(payload[grading_scheme_type]);

                    grading_scheme.uuid = payload.rubric_uuid;
                    grading_scheme.activity_id = payload.id;
                    grading_scheme.activity_uuid = payload.uuid;

                    await context.dispatch(
                        `GradingScheme${temp ? 'Templates' : 's'}/hydrate`,
                        { grading_scheme },
                        { root: true }
                    );
                }

                if (has(payload, settings)) {
                    // Component that loads time needs a Date object, set time to generic date

                    if (payload[settings].week_due && payload[settings].day_due && payload[settings].time_due) {
                        payload[settings].time_due = new Date(`January 1, 1970 ${payload[settings].time_due}`);
                    }

                    if (
                        payload[settings].week_visible &&
                        payload[settings].day_visible &&
                        payload[settings].time_visible
                    ) {
                        payload[settings].time_visible = new Date(`January 1, 1970 ${payload[settings].time_visible}`);
                    }
                }

                const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

                // Fallback to UTC if timezone not available
                if (payload[settings].due_at) {
                    payload[settings].due_at = timezone
                        ? moment.utc(payload[settings].due_at).tz(timezone).toDate()
                        : moment.utc(payload[settings].due_at).toDate();
                }

                if (payload[settings].visible_at) {
                    payload[settings].visible_at = timezone
                        ? moment.utc(payload[settings].visible_at).tz(timezone).toDate()
                        : moment.utc(payload[settings].visible_at).toDate();
                }

                if (payload?.contentable && !payload?.activity_content) {
                    payload.activity_content = payload.contentable;
                }

                await context.dispatch('add', payload);

                if (payload.activity_type === 'presentation') {
                    context.commit('CONTENT_PAGES', {
                        uuid: payload.uuid,
                        content_pages: payload.activity_content,
                    });
                }
            },

            updateItem({ commit, dispatch, getters }, item) {
                const get = getters[`${item.uuid}/get`];

                if (item.setting && item.value === false) {
                    dispatch('handleSettings', {
                        field: item.field,
                        target: item.uuid,
                        item,
                    });

                    return;
                }

                if (item.field === 'displayDueDates') {
                    dispatch(`${name}/${item.uuid}/parseSettings`, {}, { root: true });
                }

                if (item.field === 'prerequisite_type' && !item.value) {
                    const target = get('prerequisite_uuid');

                    dispatch('removeInversePrerequisites', {
                        target,
                        initiator: item,
                    });

                    dispatch(`${item.uuid}/removePrerequisite`);

                    return;
                }

                if (item.field === 'prerequisite_uuid') {
                    dispatch('setInversePrerequisites', {
                        initiator: item.uuid,
                        recipient: item.value,
                    });
                }

                commit('EDIT', item);
            },

            setInversePrerequisites({ dispatch, rootState }, { initiator, recipient }) {
                const template = name.includes('Temp');
                const sectionNamespace = template ? 'SectionTemplates' : 'Sections';
                const isActivity = has(rootState[name], recipient);

                if (isActivity) {
                    return dispatch(`${recipient}/setInversePrerequisites`, initiator);
                }

                return dispatch(`${sectionNamespace}/${recipient}/setInversePrerequisites`, initiator, { root: true });
            },

            removeInversePrerequisites({ dispatch, rootState }, { target, initiator }) {
                const template = name.includes('Temp');
                const sectionNamespace = template ? 'SectionTemplates' : 'Sections';
                const isActivity = has(rootState[name], target);
                const targetNamespace = isActivity ? name : sectionNamespace;

                if (isActivity) {
                    dispatch(`${target}/removeInversePrerequisites`, initiator.uuid);
                } else {
                    dispatch(`${targetNamespace}/${target}/removeInversePrerequisites`, initiator.uuid, { root: true });
                }
            },

            fetchTypes() {
                return new Promise(resolve => {
                    axios.get('/api/activity-types').then(response => {
                        const { data } = response.data;

                        resolve(data);
                    });
                });
            },

            addActivityContent(context, item) {
                context.commit('CONTENT', item);
            },

            addNestedItemContent(context, item) {
                context.commit('SETTINGS', item);
            },

            async verifyActivity(context, item) {
                const { data } = await axios
                    .get(`/api/courses/${item.course_id}/activities/${item.activity_id}/verify`)
                    .then(resp => resp.data);

                const uuid = context.getters.getByKey({
                    keyName: 'id',
                    key: Number(item.activity_id),
                    field: 'uuid',
                });

                const content = context.getters.getByKey({
                    keyName: 'id',
                    key: Number(item.activity_id),
                    field: 'activity_content',
                });

                context.commit('EDIT', {
                    uuid,
                    field: 'activity_content',
                    value: {
                        ...content,
                        threads: data.threads,
                        replies: data.replies,
                    },
                });

                if (data.completed && !data.marked_completed_at) {
                    context.commit('EDIT', {
                        uuid,
                        field: 'softCompleted',
                        value: true,
                    });

                    return data;
                }

                return data;
            },

            async verifyQuiz(context, item) {
                const { data } = await axios
                    .get(`/api/courses/${item.course_id}/activities/${item.activity_id}/verify`)
                    .then(resp => resp.data);

                const uuid = context.getters.getByKey({
                    keyName: 'id',
                    key: Number(item.activity_id),
                    field: 'uuid',
                });

                if (data.completed && !data.marked_completed_at && uuid) {
                    context.commit('EDIT', {
                        uuid,
                        field: 'softCompleted',
                        value: true,
                    });

                    return data;
                }

                return data;
            },

            async nextActivity(context, payload) {
                let nextActivityIndex;
                const currentUuid = context.getters.getByKey({
                    keyName: 'id',
                    key: Number(payload.id),
                    field: 'uuid',
                });

                const course = await axios.get(`/api/courses/${payload.courseId}`);

                const activities = course.data.data.activities;

                activities.forEach((activity, index) => {
                    if (activity.uuid === currentUuid) {
                        nextActivityIndex = index + 1;
                    }
                });

                return activities[nextActivityIndex];
            },

            handleSettings(context, { target, field, item }) {
                const template = name.toLowerCase().includes('temp');
                const namespace = template ? 'ActivityTemplates' : 'Activities';

                const getDispatchData = prop => {
                    // Rubrics/

                    if (prop === 'displayRubric') {
                        const rubricNamespace = template ? 'TemplateRubrics' : 'Rubrics';

                        const { rubric_uuid } = context.state[target];

                        const rubric = context.rootGetters[`${rubricNamespace}/getRubric`]({
                            target: rubric_uuid,
                        });

                        return {
                            action: `${rubricNamespace}/deleteRubric`,
                            payload: {
                                rubric,
                                message:
                                    'Confirm this message to <strong>remove</strong> the Rubric from this Activity',
                            },
                        };
                    }
                    if (prop === 'displayDueDates') {
                        return {
                            action: `${namespace}/${target}/removeDueDates`,
                            payload: {
                                message:
                                    'Confirm this message to <strong>remove</strong> Due Date settings from this Activity',
                            },
                        };
                    }

                    return null;
                };

                const module = getDispatchData(field);

                if (module) {
                    context.dispatch(module.action, { ...module.payload }, { root: true });

                    return;
                }

                context.commit('EDIT', item);
            },

            updateActivitySettings(context) {
                const activities = toArray(context.getters.all());

                activities.forEach(activity => {
                    context.dispatch(`${name}/${activity.uuid}/parseSettings`, {}, { root: true });
                });
            },

            async deleteItem({ dispatch, state }, uuid) {
                const item = state[uuid];
                const endpoint = item.type.includes('template') ? 'activity-templates' : 'activities';

                if (item.id) {
                    const response = await axios.delete(new QueryBuilder(`${endpoint}/${item.id}`).toUrl(), {
                        validateStatus(status) {
                            return status >= 200 && status < 600; // Resolve for all status codes
                        },
                    });

                    if (response.status >= 200 && response.status <= 300) {
                        await dispatch('remove', uuid);
                    }
                }
            },
        },

        mutations: {
            EDIT(state, item) {
                if (!state[item.uuid]) {
                    return;
                }
                state[item.uuid][item.field] = item.value;
            },

            DELETE_MEDIA() {
                // Used to trigger autosave
            },

            CONTENT(state, item) {
                state[item.uuid].activity_content[item.field] = item.value;
            },

            CONTENT_PAGES(state, { uuid, content_pages }) {
                state[uuid].activity_content = content_pages;
            },

            EDIT_CONTENT_PAGE_CONTENT(state, { uuid, id, content }) {
                const index = findIndex(state[uuid].activity_content, page => page.id === id);
                state[uuid].activity_content[index].content = content;
            },

            EDIT_CONTENT_PAGE_TITLE(state, { uuid, id, title }) {
                const index = findIndex(state[uuid].activity_content, page => page.id === id);
                state[uuid].activity_content[index].title = title;
            },

            EDIT_CONTENT_PAGE_ORDER(state, { uuid, id, order }) {
                const index = findIndex(state[uuid].activity_content, page => page.id === id);
                state[uuid].activity_content[index].order = order;
            },

            SETTINGS(state, item) {
                if (!state[item.uuid]) {
                    console.warn(`Attempting to set settings for non-existent activity: ${item.uuid}`);
                    return;
                }

                if (!state[item.uuid][item.type]) {
                    // Initialize missing settings container to avoid runtime errors when first writing
                    state[item.uuid][item.type] = {};
                }

                state[item.uuid][item.type][item.field] = item.value;
            },

            QUIZ_CONTENT() {
                // Used to trigger autosave
            },

            REMOVE_PROP(state, item) {
                delete state[item.uuid][item.field][item.key];
            },
        },
    });
}
